import * as Sentry from "@sentry/react";
import {
  FinixPaymentForm,
  IFinixPaymentFormHandle,
} from "components/FinixPaymentForm/FinixPaymentForm";
import { installmentScheduleTypeConstants } from "constants/account";
import { orderTypeConstants } from "constants/order";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useFinixFraudSession } from "hooks/useFinixJs";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetCustomsByOrderTypeAndAccountIdQuery } from "store/queries/customize";
import {
  useCancelFinixSplitAuthMutation,
  useCaptureFinixSplitPaymentMutation,
  useCreateFinixPaymentMutation,
  useCreateFinixSplitAuthMutation,
  useGetFinixSavedPaymentMethodQuery,
  usePayFinixRemainingWithSavedCardMutation,
} from "store/queries/finix";
import { useGetCurrentLegalDocumentsQuery } from "store/queries/legalDocuments";
import { ICreatePaymentTermsAcceptanceDto } from "types/LegalDocuments/LegalDocuments";
import { IOrder } from "types/Order/Order";
import { TermsAcceptanceContext } from "types/TermsAcceptance/TermsAcceptance";

import {
  addMonthsLikeBackend,
  addWeeksLikeBackend,
  adjustInstallmentDateForWeekend,
} from "../Overview/helpers";
import { CardAccordion } from "../PaymentSection/components/CardAccordion";
import {
  HEADER_PANEL_TEXT,
  HeaderPanel,
} from "../PaymentSection/components/HeaderPanel";
import { LegalDocumentDialog } from "../PaymentSection/components/LegalDocumentDialog";
import { PaymentSplitSection } from "../PaymentSection/components/PaymentSplitSection";
import { SavedCard } from "../PaymentSection/components/SavedCard";
import { PaymentSuccessFull } from "../PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import {
  AutopayWarningBanner,
  ContainerForm,
  StyledButton,
  StyledInputNumber,
  StyledSplitButton,
} from "../PaymentSection/styled";

const SECURE_TRANSACTIONS_TEXT = "All transactions are secure and encrypted";

const PAYMENT_PLAN_AUTHORIZATION_TEXT =
  "I authorize Clear Health to automatically charge the provided credit card for the specified (and updated if applicable) amount in the Payment Plan for each billing period";

interface IFinixPaymentSectionProps {
  order: IOrder;
  prices: {
    clearPrice: number;
    totalSaving: number;
    fullPrice: number;
  };
  finixApplicationId: string;
  finixMerchantId?: string;
  finixEnvironment: "sandbox" | "live";
  showPaymentSection: {
    paymentPlan: boolean;
    paymentSection: boolean;
    paymentSuccess: boolean;
    receiptAvailable: boolean;
  };
  setShowPaymentSection: any;
  onReturnClick?: () => void;
  onUpdateOrder?: () => void;
  isPaymentPlan?: boolean;
  isDownPaymentOnly?: boolean;
  downPaymentAmountInCents?: number;
  /** Days from date of service after which the negotiated discount expires. */
  expirationDays?: number;
  /** When true, the user is paying down an existing PaymentPlan's remaining balance.
   *  prices.clearPrice is the current remaining amount. User picks a custom amount
   *  between $1 and clearPrice and either uses a saved Finix PI or enters a new card. */
  payRemainingPrice?: boolean;
}

export function FinixPaymentSection({
  order,
  prices,
  finixApplicationId,
  finixMerchantId,
  finixEnvironment,
  showPaymentSection,
  setShowPaymentSection,
  onReturnClick,
  onUpdateOrder,
  isPaymentPlan = false,
  isDownPaymentOnly = false,
  downPaymentAmountInCents,
  expirationDays = 0,
  payRemainingPrice = false,
}: IFinixPaymentSectionProps) {
  useDocumentTitle("Payment Page");

  const { pageViewEvents } = useAnalytics();
  // Read the ref object (not its `.current`) so showError reads the freshest
  // toast instance at call time rather than capturing a possibly-null value
  // from an earlier render.
  const toastRef = useContext(ToastContext);

  // Initialize Finix.Auth for on-session fraud detection — its getSessionKey() is
  // called right before each auth/transfer mutation so the key is fresh.
  const { getFraudSessionId } = useFinixFraudSession(
    finixEnvironment,
    finixMerchantId
  );

  const firstFormRef = useRef<IFinixPaymentFormHandle>(null);
  const secondFormRef = useRef<IFinixPaymentFormHandle>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Inline payment-error banner (persists until next attempt). Backstops the
  // Toast notification, which has a 6s life and can be missed if the user is
  // focused on the form.
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [firstCardComplete, setFirstCardComplete] = useState(false);

  const { data: legalDocuments, isLoading: isLegalDocumentsFetching } =
    useGetCurrentLegalDocumentsQuery();

  const { data: contentData } = useGetCustomsByOrderTypeAndAccountIdQuery(
    { orderType: order.orderType, accountId: order.account.id },
    { skip: !order }
  );
  const { overviewPage: overviewContent } = contentData?.data || {};

  // "01/01/2999" is the placeholder date of service used when a visit isn't
  // scheduled yet — the discount-expiry banner is meaningless in that case.
  const notScheduledYet = parseDate(order.dateOfService) === "01/01/2999";

  const [openLegalDocument, setOpenLegalDocument] = useState<
    "terms" | "privacy" | null
  >(null);
  // Built in handleSubmit, consumed by the tokenize-then-charge single-card
  // flow whose async callbacks can't easily receive parameters.
  const termsAcceptanceRef = useRef<
    ICreatePaymentTermsAcceptanceDto | undefined
  >(undefined);
  const [secondCardComplete, setSecondCardComplete] = useState(false);

  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [firstCardAmount, setFirstCardAmount] = useState<number>(0);
  const [secondCardAmount, setSecondCardAmount] = useState<number>(0);
  const [preferredPaymentMethodIndex, setPreferredPaymentMethodIndex] =
    useState<0 | 1>(0);

  // Pay-remaining-price state (parity with Stripe PaymentSection)
  const [editableRemainingAmount, setEditableRemainingAmount] =
    useState<number>(payRemainingPrice ? prices.clearPrice : 0);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState<any>(null);
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(false);
  const [autopayWarningDismissed, setAutopayWarningDismissed] = useState(false);

  const {
    data: savedPaymentMethodData,
    isLoading: isSavedPaymentMethodLoading,
  } = useGetFinixSavedPaymentMethodQuery(order.id, {
    skip: !payRemainingPrice,
  });

  useEffect(() => {
    if (savedPaymentMethodData?.data && payRemainingPrice) {
      setSavedPaymentMethod(savedPaymentMethodData.data);
      setUseSavedPaymentMethod(true);
    }
  }, [savedPaymentMethodData, payRemainingPrice]);

  const [payFinixRemainingWithSavedCard] =
    usePayFinixRemainingWithSavedCardMutation();

  // Promise resolvers for tokenization (allow wrapping the callback-based form API)
  const firstCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);
  const secondCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);

  const [createFinixPayment] = useCreateFinixPaymentMutation();
  const [createFinixSplitAuth] = useCreateFinixSplitAuthMutation();
  const [captureFinixSplitPayment] = useCaptureFinixSplitPaymentMutation();
  const [cancelFinixSplitAuth] = useCancelFinixSplitAuthMutation();

  const publicContact = useCallback(() => {
    const orderData = order as any;
    const provider = orderData?.providers?.[0];
    return {
      isVisible: !!(
        provider?.publicContactName &&
        provider?.publicContactRole &&
        provider?.publicContactPhone
      ),
      name: provider?.publicContactName || "",
      role: provider?.publicContactRole || "",
      phone: provider?.publicContactPhone || "",
    };
  }, [order]);

  const activePaymentPlan = useMemo(
    () => order?.paymentPlan?.find((plan) => plan?.active),
    [order]
  );

  // For pay-remaining-price: surface the existing payment plan's status (X payments
  // left, per-period installment, next due date) and the "does not replace your upcoming
  // automatic payment" warning. Only meaningful when the active plan has installments.
  const remainingInstallmentsInfo = useMemo(() => {
    const installments = activePaymentPlan?.installments;
    if (!installments?.length) return null;
    const remaining = installments.filter((inst) => !inst.paymentId);
    if (!remaining.length) return null;
    const frequencySuffix: Record<string, string> = {
      Monthly: "/mo",
      Weekly: "/weekly",
      Biweekly: "/BiWeekly",
    };
    return {
      remainingPayments: remaining.length,
      installmentAmountInCents: remaining[0]?.amountInCents ?? 0,
      nextDateDue: remaining[0]?.dateDue,
      frequencySuffix:
        frequencySuffix[activePaymentPlan?.installmentFrequency ?? "Monthly"] ??
        "/mo",
    };
  }, [activePaymentPlan]);

  const estimatedCost = useMemo(() => {
    const orderTypeAmountMap = new Map<string, number>([
      [orderTypeConstants.BARIATRICS, prices.clearPrice],
      [orderTypeConstants.BUNDLED, prices.clearPrice],
      [orderTypeConstants.PATIENT_RESPONSIBILITY, prices.fullPrice],
      [orderTypeConstants.GFE, prices.fullPrice],
      [orderTypeConstants.ED, prices.clearPrice],
    ]);
    return orderTypeAmountMap.get(order.orderType) || 0;
  }, [prices, order]);

  const initialPayment = useMemo(() => {
    const minDownPaymentPercent =
      (activePaymentPlan?.paymentPlanMinDownPaymentPercent ?? 0) || 0;
    const minDownPaymentFlat = activePaymentPlan?.paymentPlanMinDownPaymentFlat;
    return minDownPaymentFlat || estimatedCost * minDownPaymentPercent;
  }, [activePaymentPlan, estimatedCost]);

  const paymentPlanDuration = activePaymentPlan?.paymentPlanDuration;

  const installmentFrequency = (activePaymentPlan?.installmentFrequency ??
    "Monthly") as "Monthly" | "Weekly" | "Biweekly";

  const installmentFrequencySuffix = useMemo(() => {
    const suffixByFrequency: Record<string, string> = {
      Monthly: "/mo",
      Weekly: "/weekly",
      Biweekly: "/BiWeekly",
    };
    return suffixByFrequency[installmentFrequency] ?? "/mo";
  }, [installmentFrequency]);

  const installmentPaymentForPaymentPlan = useMemo(() => {
    // Set-amount plans carry a fixed per-installment amount — show it as-is
    // instead of dividing the remaining balance across the duration.
    if (
      activePaymentPlan?.calculationBasis === "InstallmentAmount" &&
      activePaymentPlan.installmentAmountInCents != null
    ) {
      return centsToDollars(activePaymentPlan.installmentAmountInCents);
    }
    if (!paymentPlanDuration) return 0;
    const remainingAmount = estimatedCost - centsToDollars(initialPayment);
    return Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
  }, [activePaymentPlan, estimatedCost, initialPayment, paymentPlanDuration]);

  const nextInstallmentDueDate = useMemo(() => {
    const { installmentScheduleType } = order.account;
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAPeriod = true;
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(order.dateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();

      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAPeriod = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }

    // Mirror generateInstallments (used by the Home timeline): use backend-style
    // date math and shift weekend due dates so the dates shown here match.
    const startNativeDate = startDate.toDate();
    const installments = Array.from(
      { length: paymentPlanDuration || 0 },
      (_, index) => {
        const periodsFromStart = addAPeriod ? index + 1 : index;
        let dueNative: Date;
        if (installmentFrequency === "Weekly") {
          dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, periodsFromStart)
          );
        } else if (installmentFrequency === "Biweekly") {
          dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, periodsFromStart * 2)
          );
        } else {
          dueNative = adjustInstallmentDateForWeekend(
            addMonthsLikeBackend(startNativeDate, periodsFromStart)
          );
        }
        return dayjs(dueNative).format("MM/DD/YYYY");
      }
    );
    return installments[0];
  }, [order, paymentPlanDuration, installmentFrequency]);

  const totalAmountInCents = useMemo(() => {
    if (payRemainingPrice) {
      return Math.round(editableRemainingAmount * 100);
    }
    if (isPaymentPlan && downPaymentAmountInCents) {
      return downPaymentAmountInCents;
    }
    return Math.round(prices.clearPrice * 100);
  }, [
    payRemainingPrice,
    editableRemainingAmount,
    isPaymentPlan,
    downPaymentAmountInCents,
    prices.clearPrice,
  ]);

  const totalPaymentAmount = useMemo(
    () => centsToDollars(totalAmountInCents),
    [totalAmountInCents]
  );

  const displayAmount = useMemo(() => {
    if (payRemainingPrice) {
      return prices.clearPrice;
    }
    if (isPaymentPlan && downPaymentAmountInCents) {
      return centsToDollars(downPaymentAmountInCents);
    }
    return prices.clearPrice;
  }, [
    payRemainingPrice,
    isPaymentPlan,
    downPaymentAmountInCents,
    prices.clearPrice,
  ]);

  const headerTitle = useMemo(() => {
    if (payRemainingPrice) {
      return "Remaining amount";
    }
    if (isPaymentPlan) {
      return isDownPaymentOnly ? "Down Payment" : "Initial Payment";
    }
    return "Checkout";
  }, [payRemainingPrice, isPaymentPlan, isDownPaymentOnly]);

  const handleAmountChange = (e: any): void => {
    const value =
      typeof e?.value === "number" ? e.value : parseFloat(e?.target?.value);
    if (!Number.isNaN(value) && value >= 1 && value <= prices.clearPrice) {
      setEditableRemainingAmount(value);
    }
    if (Number.isNaN(value)) {
      setEditableRemainingAmount(1);
    }
  };

  // Keep firstCardAmount in sync with the split
  useEffect(() => {
    if (isSplitPayment) {
      setFirstCardAmount(totalPaymentAmount - secondCardAmount);
    } else {
      setFirstCardAmount(totalPaymentAmount);
    }
  }, [secondCardAmount, totalPaymentAmount, isSplitPayment]);

  const handleAddSecondCard = () => {
    setIsSplitPayment(true);
    const half = Math.floor(totalPaymentAmount / 2);
    setSecondCardAmount(half);
    setFirstCardAmount(totalPaymentAmount - half);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handleRemoveSecondCard = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsSplitPayment(false);
    setSecondCardAmount(0);
    setFirstCardAmount(totalPaymentAmount);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handlePaymentSplitFirstCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(totalPaymentAmount - clamped);
  };

  const handlePaymentSplitSecondCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(clamped);
  };

  const payNowDisabled = useMemo(() => {
    if (!termsAccepted) {
      return true;
    }
    if (payRemainingPrice) {
      // Saved PI path: amount must be valid, nothing else required
      if (useSavedPaymentMethod && savedPaymentMethod) {
        return (
          editableRemainingAmount < 1 ||
          editableRemainingAmount > prices.clearPrice ||
          isProcessing
        );
      }
      // New card path: amount valid + card fields complete
      return (
        !firstCardComplete ||
        editableRemainingAmount < 1 ||
        editableRemainingAmount > prices.clearPrice ||
        isProcessing
      );
    }
    if (isSplitPayment) {
      return (
        !firstCardComplete ||
        !secondCardComplete ||
        secondCardAmount <= 0 ||
        secondCardAmount >= totalPaymentAmount ||
        isProcessing
      );
    }
    return !firstCardComplete || isProcessing;
  }, [
    payRemainingPrice,
    useSavedPaymentMethod,
    savedPaymentMethod,
    editableRemainingAmount,
    prices.clearPrice,
    isSplitPayment,
    firstCardComplete,
    secondCardComplete,
    secondCardAmount,
    totalPaymentAmount,
    isProcessing,
    termsAccepted,
  ]);

  const debouncedPageViewEvents = useMemo(
    () =>
      debounce((orderData: any, event: string) => {
        pageViewEvents(
          {
            accountName: orderData?.accountName,
            orderType: orderData?.orderType,
            communicationMethod: orderData?.communicationMethod,
          },
          event
        );
      }, 500),
    [pageViewEvents]
  );

  // Cancel any pending debounced analytics call on unmount to avoid a leak
  useEffect(
    () => () => debouncedPageViewEvents.cancel(),
    [debouncedPageViewEvents]
  );

  const showError = useCallback(
    (summary: string, detail: string) => {
      toastRef?.current?.show({
        severity: "error",
        summary,
        detail,
        life: 6000,
      });
      setPaymentError(`${summary}: ${detail}`);
    },
    [toastRef]
  );

  const buildAcceptanceSnapshot = (
    context: TermsAcceptanceContext
  ): Record<string, unknown> | undefined => {
    const displayedText: Record<string, string> = {
      secureTransactions: SECURE_TRANSACTIONS_TEXT,
      header: headerTitle,
      amount: parsePrice(displayAmount),
    };

    if (context === TermsAcceptanceContext.DownPayment && !isDownPaymentOnly) {
      displayedText.paymentPlanAuthorization = PAYMENT_PLAN_AUTHORIZATION_TEXT;
      displayedText.paymentPlanAdjustment = HEADER_PANEL_TEXT;
      displayedText.paymentPlanInstallments = `${parsePrice(
        installmentPaymentForPaymentPlan
      )}${installmentFrequencySuffix}  ${paymentPlanDuration} payments`;
      displayedText.nextPaymentDue = `Next payment due ${nextInstallmentDueDate}`;
    }

    if (
      context === TermsAcceptanceContext.PayRemaining &&
      remainingInstallmentsInfo
    ) {
      displayedText.paymentPlanInstallments = `${
        remainingInstallmentsInfo.remainingPayments
      } payments left → ${parsePrice(
        centsToDollars(remainingInstallmentsInfo.installmentAmountInCents)
      )}${remainingInstallmentsInfo.frequencySuffix}`;
      if (remainingInstallmentsInfo.nextDateDue) {
        const nextDate = dayjs
          .utc(remainingInstallmentsInfo.nextDateDue)
          .format("M/D/YYYY");
        displayedText.nextPaymentScheduled = `Next payment scheduled for ${nextDate}`;
        if (editableRemainingAmount < prices.clearPrice) {
          displayedText.autopayNotice = `This payment does not replace your upcoming automatic payment on ${nextDate}.`;
        }
      }
    }

    return { displayedText };
  };

  const buildTermsAcceptance = async (
    context: TermsAcceptanceContext
  ): Promise<ICreatePaymentTermsAcceptanceDto | undefined> => {
    const termsAndConditionsVersionId =
      legalDocuments?.data?.termsAndConditions?.id;
    const privacyPolicyVersionId = legalDocuments?.data?.privacyPolicy?.id;
    if (!termsAndConditionsVersionId || !privacyPolicyVersionId) {
      return undefined;
    }
    let ipAddress: string | undefined;
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      ipAddress = data.ip;
    } catch {
      // IP lookup failed — proceed without it
    }
    return {
      acceptanceContext: context,
      termsAndConditionsVersionId,
      privacyPolicyVersionId,
      acceptanceSnapshot: buildAcceptanceSnapshot(context),
      ipAddress,
      userAgent: navigator.userAgent,
    };
  };

  // === Single-card (non-split) flow — unchanged from before ===

  const handleSingleCardTokenSuccess = async (token: string) => {
    try {
      // Pay-remaining with a new card: backend routes to processFinixPayRemainingPrice
      // when payRemainingPrice=true. savePaymentMethod=true when the user selected the
      // new card as the preferred card for future autopays (index 1) on a partial payment.
      const isPartialRemaining =
        payRemainingPrice && editableRemainingAmount !== prices.clearPrice;
      const savePaymentMethod =
        payRemainingPrice &&
        preferredPaymentMethodIndex === 1 &&
        isPartialRemaining;

      const result = await createFinixPayment({
        orderId: order.id,
        token,
        amountInCents: totalAmountInCents,
        saveForFuture: isPaymentPlan,
        payRemainingPrice,
        savePaymentMethod,
        fraudSessionId: getFraudSessionId(),
        termsAcceptance: termsAcceptanceRef.current,
      }).unwrap();

      if (result.success) {
        pageViewEvents(
          {
            accountName: order.account?.name,
            orderType: order.orderType,
            communicationMethod: order.patient?.preferredContactMethod,
          },
          events.PURCHASE
        );

        if (onUpdateOrder) {
          onUpdateOrder();
        }

        setShowPaymentSection({
          paymentPlan: false,
          paymentSection: false,
          paymentSuccess: true,
          receiptAvailable: true,
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment processing failed. Please try again.";

      Sentry.captureMessage("Finix payment processing failed", {
        level: "error",
        tags: {
          operation: "finix_payment_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSingleCardTokenError = (error: string) => {
    setIsProcessing(false);
    showError("Card Error", error);
  };

  // === Split-payment flow ===
  // The FinixPaymentForm uses callbacks (onTokenSuccess/onTokenError). To sequence
  // two tokenizations + backend calls, we wire each form's callbacks to a ref-held
  // Promise resolver, then await each tokenization in order.

  const handleFirstCardTokenSuccess = (token: string) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.resolve(token);
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      // Orphan tokenization — resolver was already consumed or never set. Reset UI so user can retry.
      Sentry.captureMessage("Finix split: orphan first-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    } else {
      // Not in a split flow — fall through to single-card handling
      handleSingleCardTokenSuccess(token);
    }
  };

  const handleFirstCardTokenError = (error: string) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.reject(new Error(error));
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      Sentry.captureMessage("Finix split: orphan first-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    } else {
      handleSingleCardTokenError(error);
    }
  };

  const handleSecondCardTokenSuccess = (token: string) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.resolve(token);
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    }
  };

  const handleSecondCardTokenError = (error: string) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.reject(new Error(error));
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    }
  };

  const tokenizeFirstCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      firstCardTokenResolverRef.current = { resolve, reject };
      if (firstFormRef.current) {
        firstFormRef.current.submit();
      } else {
        firstCardTokenResolverRef.current = null;
        reject(new Error("First card form is not ready"));
      }
    });

  const tokenizeSecondCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      secondCardTokenResolverRef.current = { resolve, reject };
      if (secondFormRef.current) {
        secondFormRef.current.submit();
      } else {
        secondCardTokenResolverRef.current = null;
        reject(new Error("Second card form is not ready"));
      }
    });

  const handleSplitPayment = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    const firstAmountCents = Math.round(firstCardAmount * 100);
    const secondAmountCents = Math.round(secondCardAmount * 100);

    let authResponse: {
      success: boolean;
      authorizationIds: [string, string];
      paymentInstrumentIds: [string, string];
      buyerIdentityId: string;
    };

    try {
      // Tokenize both cards sequentially
      const token1 = await tokenizeFirstCard();
      const token2 = await tokenizeSecondCard();

      // Backend authorizes both cards
      authResponse = await createFinixSplitAuth({
        orderId: order.id,
        token1,
        amountInCents1: firstAmountCents,
        token2,
        amountInCents2: secondAmountCents,
        saveForFuture: isPaymentPlan,
        paymentPlan: isPaymentPlan,
        fraudSessionId: getFraudSessionId(),
      }).unwrap();
    } catch (error: any) {
      const failedCard = error?.data?.failedCard;
      const reason = error?.data?.reason || error?.data?.message;
      const errorMessage = failedCard
        ? `Card ${failedCard} could not be authorized${
            reason ? `: ${reason}` : ""
          }. Please try a different card.`
        : error?.data?.message ||
          error?.message ||
          "Payment failed. Please check your card details and try again.";

      Sentry.captureMessage("Finix split payment authorize failed", {
        level: "error",
        tags: { operation: "finix_split_auth_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // Capture both authorizations
    try {
      await captureFinixSplitPayment({
        orderId: order.id,
        authorizationIds: authResponse.authorizationIds,
        amounts: [firstAmountCents, secondAmountCents],
        paymentInstrumentIds: authResponse.paymentInstrumentIds,
        paymentPlan: isPaymentPlan,
        preferredPaymentMethodIndex,
        saveForFuture: isPaymentPlan,
        termsAcceptance,
      }).unwrap();
    } catch (error: any) {
      // Capture failed — try to clean up the auths we created
      try {
        await cancelFinixSplitAuth({
          orderId: order.id,
          authorizationIds: authResponse.authorizationIds,
          failureReason: "capture_failed",
        }).unwrap();
      } catch (cancelError) {
        Sentry.captureMessage(
          "Failed to cancel Finix split auths after capture failure",
          {
            level: "warning",
            tags: {
              operation: "finix_split_cancel_after_capture_fail",
              orderId: order.id,
            },
            extra: { cancelError: JSON.stringify(cancelError) },
          }
        );
      }

      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Payment capture failed. Please try again.";

      Sentry.captureMessage("Finix split payment capture failed", {
        level: "error",
        tags: { operation: "finix_split_capture_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // Success
    pageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.PURCHASE
    );

    if (onUpdateOrder) {
      onUpdateOrder();
    }

    setShowPaymentSection({
      paymentPlan: false,
      paymentSection: false,
      paymentSuccess: true,
      receiptAvailable: true,
    });
    setIsProcessing(false);
  };

  // === Pay-remaining with saved Finix PI ===
  // No tokenization — backend reuses the PI stored on the active PaymentPlan.
  const handleSavedCardPayRemaining = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    try {
      await payFinixRemainingWithSavedCard({
        orderId: order.id,
        amountInCents: totalAmountInCents,
        fraudSessionId: getFraudSessionId(),
        termsAcceptance,
      }).unwrap();

      pageViewEvents(
        {
          accountName: order.account?.name,
          orderType: order.orderType,
          communicationMethod: order.patient?.preferredContactMethod,
        },
        events.PURCHASE
      );

      if (onUpdateOrder) onUpdateOrder();

      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: true,
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment failed. Please try again or use a different card.";

      Sentry.captureMessage("Finix pay-remaining (saved PI) failed", {
        level: "error",
        tags: {
          operation: "finix_pay_remaining_saved_pi_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setPaymentError(null);

    let context = TermsAcceptanceContext.FullPayment;
    if (payRemainingPrice) {
      context = TermsAcceptanceContext.PayRemaining;
    } else if (isPaymentPlan) {
      context = TermsAcceptanceContext.DownPayment;
    }
    const termsAcceptance = await buildTermsAcceptance(context);
    // Cache for the single-card flow; its onTokenSuccess callback fires later
    // and can't easily receive parameters.
    termsAcceptanceRef.current = termsAcceptance;

    debouncedPageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.CLICK_PURCHASE
    );

    // Pay-remaining with saved PI — no tokenization needed
    if (payRemainingPrice && useSavedPaymentMethod && savedPaymentMethod) {
      handleSavedCardPayRemaining(termsAcceptance).catch((err) => {
        Sentry.captureException(err, {
          tags: {
            operation: "finix_pay_remaining_saved_pi_unhandled",
            orderId: order.id,
          },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    if (isSplitPayment) {
      handleSplitPayment(termsAcceptance).catch((err) => {
        Sentry.captureException(err, {
          tags: { operation: "finix_split_unhandled", orderId: order.id },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    // Single-card path — also handles pay-remaining with a new card
    if (firstFormRef.current) {
      firstFormRef.current.submit();
    } else {
      setIsProcessing(false);
      showError("Error", "Card form is not ready. Please try again.");
    }
  };

  const handleOpenPrivacyPolicy = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("privacy");
  };

  const handleOpenTermsAndConditions = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("terms");
  };

  if (showPaymentSection.paymentSuccess) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={order.externalId || ""}
        receiptAvailable={showPaymentSection.receiptAvailable}
      />
    );
  }

  // Stripe parity: a partial pay-remaining (amount < clearPrice) shows the preferred-card radio
  // so the patient can choose whether to switch the saved card to the new card after the charge.
  const isPartialPayRemaining =
    payRemainingPrice && editableRemainingAmount !== prices.clearPrice;

  return (
    <ContainerForm>
      {isPaymentPlan && !isDownPaymentOnly && <HeaderPanel />}
      <div className="flex flex-column">
        <span className="title">
          <span>{headerTitle}</span>
          <span>{parsePrice(displayAmount)}</span>
        </span>
      </div>
      {isPaymentPlan && !isDownPaymentOnly && (
        <div className="flex flex-column my-3 align-items-center">
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            {parsePrice(installmentPaymentForPaymentPlan)}
            {installmentFrequencySuffix}
            {"   "} <span className="ml-2">{paymentPlanDuration} payments</span>
          </p>
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            Next payment due {nextInstallmentDueDate}
          </p>
        </div>
      )}

      {payRemainingPrice && remainingInstallmentsInfo && (
        <div className="flex flex-column my-3 align-items-center">
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            {remainingInstallmentsInfo.remainingPayments} payments left
            <span className="mx-2">→</span>
            {parsePrice(
              centsToDollars(remainingInstallmentsInfo.installmentAmountInCents)
            )}
            {remainingInstallmentsInfo.frequencySuffix}
          </p>
          {remainingInstallmentsInfo.nextDateDue && (
            <p className="text-sm font-normal" style={{ color: "#64636E" }}>
              Next payment scheduled for{" "}
              {dayjs
                .utc(remainingInstallmentsInfo.nextDateDue)
                .format("M/D/YYYY")}
            </p>
          )}
        </div>
      )}
      {payRemainingPrice &&
        remainingInstallmentsInfo?.nextDateDue &&
        editableRemainingAmount < prices.clearPrice &&
        !autopayWarningDismissed && (
          <AutopayWarningBanner role="alert">
            <i
              className="pi pi-info-circle mt-1"
              style={{ color: "#DC6803" }}
            />
            <span className="text-sm text-left flex-1">
              This payment does not replace your upcoming automatic payment on{" "}
              {dayjs
                .utc(remainingInstallmentsInfo.nextDateDue)
                .format("M/D/YYYY")}
              .
            </span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setAutopayWarningDismissed(true)}
              className="close-button"
            >
              <i className="pi pi-times" style={{ fontSize: "0.875rem" }} />
            </button>
          </AutopayWarningBanner>
        )}
      {/* Pay-remaining: custom amount input */}
      {payRemainingPrice && (
        <div className="flex flex-column mt-4 mb-4">
          <label htmlFor="custom-amount" className="text-sm mb-1">
            Custom Amount:
          </label>
          <StyledInputNumber
            id="custom-amount"
            value={editableRemainingAmount}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={1}
            max={prices.clearPrice}
            onValueChange={handleAmountChange}
          />
        </div>
      )}

      {overviewContent?.discountExpires &&
        !payRemainingPrice &&
        !isPaymentPlan &&
        !notScheduledYet && (
          <span className="mt-1 font-semibold text-base line-height-3 text-center warning">
            Discount expires on{" "}
            {dayjs
              .utc(order.dateOfService)
              .add(expirationDays, "day")
              .format("MMMM D, YYYY")}
          </span>
        )}

      {/* Pay-remaining: saved card (from PaymentPlan.finixPaymentInstrumentId) */}
      {payRemainingPrice && savedPaymentMethod && (
        <>
          <SavedCard
            savedPaymentMethod={savedPaymentMethod}
            showFooter={!useSavedPaymentMethod && isPartialPayRemaining}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            radioValue={0}
          />
          {useSavedPaymentMethod && (
            <Button
              className="p-button-text p-0 mb-4"
              onClick={(e) => {
                e.preventDefault();
                setUseSavedPaymentMethod(!useSavedPaymentMethod);
              }}
            >
              <i className="pi pi-plus-circle mr-2" />
              Use a different card
            </Button>
          )}
        </>
      )}

      {/* Pay-remaining with a new (not saved) card — render the Finix form inside a CardAccordion
          that also carries the "use this card for future payments" radio on partial payments. */}
      {payRemainingPrice &&
        !useSavedPaymentMethod &&
        !isSavedPaymentMethodLoading && (
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="New Card Information"
              isFirstCard={false}
              showFooter={!!savedPaymentMethod && isPartialPayRemaining}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={0}
              handleRemoveSecondCard={
                savedPaymentMethod
                  ? () => {
                      setUseSavedPaymentMethod(!useSavedPaymentMethod);
                      setPreferredPaymentMethodIndex(0);
                    }
                  : undefined
              }
              footerText="Use this card for future payments"
            >
              <FinixPaymentForm
                ref={firstFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setFirstCardComplete}
                onTokenSuccess={handleFirstCardTokenSuccess}
                onTokenError={handleFirstCardTokenError}
              />
            </CardAccordion>
          </div>
        )}

      {/* Non-pay-remaining: first card — always rendered inside a single CardAccordion so the
          FinixPaymentForm keeps a stable position in the React tree. Toggling isSplitPayment only
          flips showStylings/showFooter; the form is NOT remounted, so card input the user already
          entered is preserved when they click "Split payment between two cards". Mirrors the Stripe
          PaymentSection pattern. (Pay-remaining has its own card-input branch above.) */}
      {!payRemainingPrice && (
        <div className={`sensitive${isSplitPayment ? "" : " mt-4"}`}>
          <CardAccordion
            showStylings={isSplitPayment}
            title="First Card"
            isFirstCard
            showFooter={isPaymentPlan && isSplitPayment}
            radioValue={0}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            cardAmount={firstCardAmount}
          >
            <FinixPaymentForm
              ref={firstFormRef}
              applicationId={finixApplicationId}
              environment={finixEnvironment}
              onCardComplete={setFirstCardComplete}
              onTokenSuccess={handleFirstCardTokenSuccess}
              onTokenError={handleFirstCardTokenError}
            />
          </CardAccordion>
        </div>
      )}

      {/* Add/Remove second card — hidden in pay-remaining mode (split payment is not supported
          for pay-remaining, matching Stripe behavior in PaymentSection.tsx:1287) */}
      {!payRemainingPrice && !isSplitPayment && (
        <StyledSplitButton
          onClick={handleAddSecondCard}
          disabled={isProcessing}
        >
          <i
            className="pi pi-plus-circle"
            style={{ marginRight: "4px", marginTop: "2px" }}
          />
          <div className="flex flex-column ml-2 text-left gap-1">
            <span className="font-semibold text-sm">
              Split payment between two cards
            </span>
            <span className="font-normal text-xs">
              Divide your {parsePrice(displayAmount)} payment across two
              different payment methods
            </span>
          </div>
        </StyledSplitButton>
      )}
      {!payRemainingPrice && isSplitPayment && (
        <>
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="Second Card"
              isFirstCard={false}
              showFooter={isPaymentPlan && isSplitPayment}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={secondCardAmount}
              handleRemoveSecondCard={handleRemoveSecondCard}
              isProcessing={isProcessing}
            >
              <FinixPaymentForm
                ref={secondFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setSecondCardComplete}
                onTokenSuccess={handleSecondCardTokenSuccess}
                onTokenError={handleSecondCardTokenError}
              />
            </CardAccordion>
          </div>

          <PaymentSplitSection
            firstCardAmount={firstCardAmount}
            secondCardAmount={secondCardAmount}
            totalPaymentAmount={totalPaymentAmount}
            onFirstCardAmountChange={handlePaymentSplitFirstCardChange}
            onSecondCardAmountChange={handlePaymentSplitSecondCardChange}
          />
        </>
      )}

      <div className="flex align-items-start gap-2 mt-2 text-left">
        <Checkbox
          inputId="finix-payment-terms-acceptance"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.checked ?? false)}
          className="mt-1 flex-shrink-0"
        />
        <label
          htmlFor="finix-payment-terms-acceptance"
          className="text-sm gray font-normal cursor-pointer m-0 line-height-3"
        >
          {`By clicking "Pay Now", I confirm that I have read and agree to the`}{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenPrivacyPolicy}
          >
            Privacy Policy
          </Button>
          {legalDocuments?.data?.privacyPolicy && (
            <>
              {" "}
              (version: {legalDocuments.data.privacyPolicy.version}, effective
              from {parseDate(legalDocuments.data.privacyPolicy.effectiveAt)})
            </>
          )}{" "}
          and{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenTermsAndConditions}
          >
            Terms and Conditions
          </Button>
          {legalDocuments?.data?.termsAndConditions && (
            <>
              {" "}
              (version: {legalDocuments.data.termsAndConditions.version},
              effective from{" "}
              {parseDate(
                legalDocuments.data.termsAndConditions.effectiveAt,
                false,
                true
              )}
              )
            </>
          )}
        </label>
      </div>

      {isPaymentPlan && (
        <p className="text-sm gray mt-2 text-left font-normal">
          I authorize Clear Health to automatically charge the provided credit
          card for the specified (and updated if applicable) amount in the
          Payment Plan for each billing period
        </p>
      )}

      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        All transactions are secure and encrypted
      </p>

      {paymentError && (
        <div
          role="alert"
          data-testid="finix-payment-error-banner"
          className="p-3 mt-2 border-1 border-round"
          style={{
            backgroundColor: "#fdecea",
            borderColor: "#f44336",
            color: "#611a15",
          }}
        >
          <i className="pi pi-exclamation-triangle mr-2" />
          <span className="text-sm">{paymentError}</span>
        </div>
      )}

      <StyledButton
        type="submit"
        loading={isProcessing}
        data-testid="button-submit-pay-now"
        className="w-full justify-content-center mt-2"
        onClick={handleSubmit}
        disabled={payNowDisabled}
      >
        <span className="font-bold">Pay Now</span>
      </StyledButton>

      {onReturnClick && (
        <div className="flex align-items-center justify-content-center mt-3">
          <Button
            className="p-button-text mb-0 p-button-secondary p-0"
            onClick={onReturnClick}
          >
            <p className="text-sm purple font-semibold">Back to details</p>
          </Button>
        </div>
      )}

      <LegalDocumentDialog
        visible={openLegalDocument === "terms"}
        onClose={() => setOpenLegalDocument(null)}
        header="Terms of Use"
        content={legalDocuments?.data?.termsAndConditions?.content}
        isLoading={isLegalDocumentsFetching}
      />
      <LegalDocumentDialog
        visible={openLegalDocument === "privacy"}
        onClose={() => setOpenLegalDocument(null)}
        header="Privacy Policy"
        content={legalDocuments?.data?.privacyPolicy?.content}
        isLoading={isLegalDocumentsFetching}
      />
    </ContainerForm>
  );
}
